import { type ComponentProps, useContext } from 'react'
import { clsx } from 'clsx'
import { AnimatePresence, motion, type Transition } from 'motion/react'
import { CrossIcon } from '../../icons/CrossIcon'
import { framerTransitions } from '../../theme/transitions'
import { IconButton } from '../Button/IconButton'
import { Card } from '../Card/Card'
import { Heading as UIHeading } from '../Heading/Heading'
import { Dialog } from './Dialog'
import {
  CLOSE_BUTTON_SIZE,
  closeButton,
  content,
  heading,
  largeDialogOverlay,
  scrollableInnerContent,
} from './LargeDialog.css'

const MotionCard = motion(Card.Root)

const ANIMATE_TRANSITION: Transition = {
  ...framerTransitions.easeInOutCubic,
  duration: framerTransitions.defaultDuration,
}

type ContentProps = ComponentProps<typeof Dialog.Content>

// Large modal, full-screen on mobile. Uses `Root` and `Trigger` from default `Dialog`
function Content({
  children,
  centerContent = true,
  className,
  frostedOverlay = true,
  ...forwardedProps
}: ContentProps) {
  const isClosing = useContext(Dialog.IsClosingContext)
  return (
    <AnimatePresence>
      {!isClosing && (
        <Dialog.Content
          centerContent={centerContent}
          frostedOverlay={frostedOverlay}
          scrollableOverlay={true}
          overlayClassName={largeDialogOverlay}
          asChild={true}
          closeOnPointerDownOutside={true}
          {...forwardedProps}
        >
          <MotionCard
            className={clsx(content, className)}
            initial={{ opacity: 0, y: '2vh' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '2vh' }}
            transition={ANIMATE_TRANSITION}
          >
            {children}
          </MotionCard>
        </Dialog.Content>
      )}
    </AnimatePresence>
  )
}

function Heading({ children, className, ...forwardedProps }: ComponentProps<typeof UIHeading>) {
  return (
    <Dialog.Title asChild>
      <UIHeading
        as="h2"
        variant={{ _: 'standard.18', md: 'standard.24' }}
        className={clsx(heading, className)}
        {...forwardedProps}
      >
        {children}
      </UIHeading>
    </Dialog.Title>
  )
}

function Root({ ...forwardedProps }: ComponentProps<typeof Dialog.Root>) {
  return <Dialog.Root closingDuration={framerTransitions.defaultDuration * 1000} {...forwardedProps} />
}

function ScrollableInnerContent({ children, className, ...forwardedProps }: ComponentProps<'div'>) {
  return (
    <div className={clsx(scrollableInnerContent, className)} {...forwardedProps}>
      {children}
    </div>
  )
}

function CloseIconButton({ ...props }: ComponentProps<typeof IconButton>) {
  return (
    <Dialog.Close asChild>
      <IconButton
        variant="secondary"
        Icon={<CrossIcon size={CLOSE_BUTTON_SIZE} />}
        className={closeButton}
        aria-label="Close"
        {...props}
      />
    </Dialog.Close>
  )
}

export const LargeDialog = {
  Content,
  Heading,
  ScrollableInnerContent,
  Root,
  CloseIconButton,
}
